import { AbstractCommand } from './AbstractCommand';
import { VideoEditor } from '../VideoEditor';
import { Element } from '../models/Element';
import { Track } from '../models/Track';
import { TimelineMode } from './TimelineMode';
import { EditorChangeType } from '../types';
import { VideoEditorLogger } from '../../utility/VideoEditorLogger';

/**
 * Command for adding media elements to the timeline with collision detection and track management.
 * Supports different TimelineMode behaviors:
 * - DEFAULT: Creates new track on collision
 * - RIPPLE: Shifts subsequent elements to make room
 * - OVERLAP: Allows overlapping elements
 */
export class AddMediaElementCommand extends AbstractCommand {
  protected element: Element;
  protected targetTrackId?: string;
  protected finalTrackId?: string;
  protected elementAdded: boolean = false;
  protected trackCreated: boolean = false;
  protected createdTrack?: Track;
  protected mode: TimelineMode;

  constructor(
    editor: VideoEditor,
    element: Element,
    targetTrackId?: string,
    mode: TimelineMode = TimelineMode.DEFAULT,
  ) {
    super(editor);
    this.element = element;
    this.targetTrackId = targetTrackId;
    this.mode = mode;
  }

  execute(): boolean {
    super.execute(); // Capture timeline snapshot

    VideoEditorLogger.logCommand(this, 'start', {
      elementId: this.element.id,
      elementType: this.element.type,
      targetTrackId: this.targetTrackId,
      mode: this.mode,
    });

    // Snap element time to frame
    this.element.update({ time: this.snapTimeToFrame(this.element.time) });

    // Find or create target track
    const targetTrack = this.findOrCreateTargetTrack();
    if (!targetTrack) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'Failed to find or create target track',
      });
      return false;
    }

    this.finalTrackId = targetTrack.id;

    // Check for collisions and handle based on mode
    if (!this.handleCollisions(targetTrack)) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'Failed to handle collisions',
      });
      return false;
    }

    // Add element to track using TrackManager
    const trackManager = this.editor.getTrackManager();
    const success = trackManager.addElement(this.element, targetTrack, this.mode);

    if (success) {
      this.elementAdded = true;
      this.editor.markDirty('add_media_element_command');
      
      // Ensure proper z-index assignment based on track positions
      this.ensureProperZIndexPositioning();
      
      this.editor.notifyObservers(EditorChangeType.ELEMENT_ADDED, {
        element: this.element,
        trackId: targetTrack.id,
        mode: this.mode,
      });

      VideoEditorLogger.logCommand(this, 'complete', {
        elementId: this.element.id,
        finalTrackId: this.finalTrackId,
        trackCreated: this.trackCreated,
      });
    } else {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'TrackManager failed to add element',
      });
    }

    return success;
  }

  undo(): boolean {
    if (!this.elementAdded) {
      VideoEditorLogger.logCommandUndo(this, false, { reason: 'No element to undo' });
      return false;
    }

    let success = true;

    try {
      const trackManager = this.editor.getTrackManager();

      // Remove the element using TrackManager
      if (this.finalTrackId) {
        const track = this.editor.getTrackById(this.finalTrackId);
        if (track) {
          const elementRemoved = trackManager.removeElement(this.element, this.mode);
          if (!elementRemoved) {
            success = false;
          }
        } else {
          success = false;
        }
      } else {
        success = false;
      }

      // If we created a track and it's now empty, remove it using TrackManager
      if (success && this.trackCreated && this.createdTrack) {
        const track = this.editor.getTrackById(this.createdTrack.id);
        if (track && track.elements.length === 0) {
          const trackRemoved = trackManager.removeTrack(this.createdTrack);
          if (!trackRemoved) {
            success = false;
          }
        }
      }

      if (success) {
        this.elementAdded = false;
        this.editor.markDirty('add_media_element_command_undo');
      }

      VideoEditorLogger.logCommandUndo(this, success);
      return success;
    } catch (error) {
      VideoEditorLogger.logCommandUndo(this, false, { error });
      return false;
    }
  }

  getDescription(): string {
    const trackInfo = this.trackCreated
      ? `new ${this.element.type} track`
      : `track ${this.finalTrackId || this.targetTrackId || 'auto'}`;
    return `Add ${this.element.type} element to ${trackInfo} (${this.mode} mode)`;
  }

  /**
   * Gets the final track ID where the element was added
   */
  getFinalTrackId(): string | undefined {
    return this.finalTrackId;
  }

  /**
   * Indicates if a new track was created during execution
   */
  getTrackCreated(): boolean {
    return this.trackCreated;
  }

  /**
   * Gets the track that was created (if any)
   */
  getCreatedTrack(): Track | undefined {
    return this.createdTrack;
  }

  /**
   * Finds an appropriate target track or creates a new one
   */
  protected findOrCreateTargetTrack(): Track | undefined {
    // If specific track ID provided, use it (unless it has collisions in DEFAULT mode)
    if (this.targetTrackId) {
      const track = this.editor.getTrackById(this.targetTrackId);
      if (track) {
        // In DEFAULT mode, if target track has collision, create new track
        if (this.mode === TimelineMode.DEFAULT && this.hasCollision(track)) {
          VideoEditorLogger.logTimelineOperation('add_media_element_collision', {
            message: `Target track ${this.targetTrackId} has collision, creating new track`,
          });
          return this.createNewTrack();
        }
        return track;
      }
      VideoEditorLogger.logTimelineOperation('add_media_element_track_not_found', {
        warning: `Target track ${this.targetTrackId} not found, will auto-select`,
      });
    }

    // Auto-select compatible track based on element type
    const compatibleTrack = this.findCompatibleTrack();
    if (compatibleTrack) {
      return compatibleTrack;
    }

    // Create new track if no compatible track found
    return this.createNewTrack();
  }

  /**
   * Finds a compatible existing track for the element
   */
  private findCompatibleTrack(): Track | undefined {
    const elementType = this.element.type;
    
    // Map element types to track types
    const trackTypeMap: Record<string, string> = {
      video: 'video',
      audio: 'audio',
      image: 'image',
      text: 'text',
    };

    const targetTrackType = trackTypeMap[elementType] || 'video';

    // Find tracks of compatible type
    const compatibleTracks = this.editor.timeline.tracks.filter(
      (track) => track.type === targetTrackType,
    );

    if (compatibleTracks.length === 0) {
      return undefined;
    }

    // For DEFAULT mode, prefer tracks with no collisions
    if (this.mode === TimelineMode.DEFAULT) {
      for (const track of compatibleTracks) {
        if (!this.hasCollision(track)) {
          return track;
        }
      }
      // If all tracks have collisions in DEFAULT mode, we'll create a new track
      return undefined;
    }

    // For RIPPLE and OVERLAP modes, use the first compatible track
    return compatibleTracks[0];
  }

  /**
   * Creates a new track for the element using TrackManager
   */
  protected createNewTrack(): Track | undefined {
    const elementType = this.element.type;
    
    // Map element types to track types
    const trackTypeMap: Record<string, 'video' | 'audio' | 'text' | 'image'> = {
      video: 'video',
      audio: 'audio',
      image: 'image',
      text: 'text',
    };

    const trackType = trackTypeMap[elementType] || 'video';

    try {
      // Use TrackManager's createTrack method
      const trackManager = this.editor.getTrackManager();
      this.createdTrack = trackManager.createTrack(trackType);
      this.trackCreated = true;

      VideoEditorLogger.logTimelineOperation('add_media_element_track_created', {
        action: 'track_created',
        trackId: this.createdTrack.id,
        trackType,
      });

      return this.createdTrack;
    } catch (error) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'Failed to create new track',
        details: error,
      });
      return undefined;
    }
  }

  /**
   * Checks if adding the element to the track would cause a collision
   */
  protected hasCollision(track: Track): boolean {
    const elementStart = this.element.time;
    const elementEnd = this.element.time + this.element.durationSeconds;
    const epsilon = 0.001; // Small tolerance for floating-point comparison

    return track.elements.some((existingElement) => {
      const existingStart = existingElement.time;
      const existingEnd = existingElement.time + existingElement.durationSeconds;

      // Check for overlap
      return (
        elementStart < existingEnd - epsilon &&
        elementEnd > existingStart + epsilon
      );
    });
  }

  /**
   * Handles collision detection and resolution based on timeline mode
   */
  private handleCollisions(track: Track): boolean {
    switch (this.mode) {
      case TimelineMode.DEFAULT:
        // In DEFAULT mode, we should have already found/created a non-colliding track
        if (this.hasCollision(track)) {
          VideoEditorLogger.logCommand(this, 'error', {
            error: 'Collision detected in DEFAULT mode with selected track',
            trackId: track.id,
          });
          return false;
        }
        return true;

      case TimelineMode.RIPPLE:
        // RIPPLE mode will be handled by TrackManager.addElement
        return true;

      case TimelineMode.OVERLAP:
        // OVERLAP mode allows collisions
        return true;

      default:
        VideoEditorLogger.logCommand(this, 'error', {
          error: `Unsupported timeline mode: ${this.mode}`,
        });
        return false;
    }
  }

  /**
   * Ensures proper z-index assignment for the newly added element based on track position
   * Uses the same logic as ensureElementZPositions from general.ts
   */
  private ensureProperZIndexPositioning(): void {
    try {
      if (!this.element || !this.finalTrackId) {
        return;
      }

      // Get the track number for z-index assignment
      const trackNumber = parseInt(this.finalTrackId);
      if (isNaN(trackNumber)) {
        VideoEditorLogger.logCommand(this, 'error', {
          message: 'Cannot parse track ID for z-index assignment',
          trackId: this.finalTrackId,
        });
        return;
      }

      // Apply z-index based on track number, following ensureElementZPositions logic
      let zIndex: number;
      
      // Special case for layout compositions (track >= 90)
      if (this.element.type === 'composition' && trackNumber >= 90) {
        zIndex = -1000;
      } else {
        // Regular elements get z_index matching their track
        zIndex = trackNumber;
      }

      // Update the element's z_index property
      this.element.update({ z_index: zIndex });

      VideoEditorLogger.logTimelineOperation('z_index_assigned', {
        elementId: this.element.id,
        elementType: this.element.type,
        trackId: this.finalTrackId,
        assignedZIndex: zIndex,
      });

    } catch (error) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'Failed to ensure z-index positioning',
        details: error,
      });
    }
  }
}